// @greenlight-spec: 19-design-system.md
import * as React from 'react';
import { Loader2 } from 'lucide-react';

import { cn } from '@/lib/utils';

export type StatusBadgeVariant =
  | 'active'
  | 'degraded'
  | 'deploying'
  | 'failed'
  | 'pending'
  | 'inactive'
  | 'granted'
  | 'revoked';

interface VariantStyle {
  dot: string;
  /** Subtle tinted pill fill (color is never the only signal — dot + label remain). */
  bg: string;
  defaultLabel: string;
}

const VARIANTS: Record<StatusBadgeVariant, VariantStyle> = {
  active: {
    dot: 'bg-[hsl(var(--status-green))]',
    bg: 'bg-[hsl(var(--status-green)/0.1)]',
    defaultLabel: 'Active',
  },
  degraded: {
    dot: 'bg-[hsl(var(--status-amber))]',
    bg: 'bg-[hsl(var(--status-amber)/0.12)]',
    defaultLabel: 'Degraded',
  },
  deploying: {
    dot: 'bg-[hsl(var(--status-amber))]',
    bg: 'bg-[hsl(var(--status-amber)/0.12)]',
    defaultLabel: 'Deploying',
  },
  failed: {
    dot: 'bg-[hsl(var(--status-red))]',
    bg: 'bg-[hsl(var(--status-red)/0.1)]',
    defaultLabel: 'Failed',
  },
  pending: {
    dot: 'bg-[hsl(var(--status-amber))]',
    bg: 'bg-[hsl(var(--status-amber)/0.12)]',
    defaultLabel: 'Pending',
  },
  inactive: {
    dot: 'bg-slate',
    bg: 'bg-muted',
    defaultLabel: 'Inactive',
  },
  granted: {
    dot: 'bg-[hsl(var(--status-green))]',
    bg: 'bg-[hsl(var(--status-green)/0.1)]',
    defaultLabel: 'Granted',
  },
  revoked: {
    dot: 'bg-[hsl(var(--status-red))]',
    bg: 'bg-[hsl(var(--status-red)/0.1)]',
    defaultLabel: 'Revoked',
  },
};

export interface StatusBadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant: StatusBadgeVariant;
}

export function StatusBadge({ variant, className, children, ...props }: StatusBadgeProps) {
  const v = VARIANTS[variant];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 font-mono text-[11px] font-medium',
        v.bg,
        'text-foreground',
        className,
      )}
      {...props}
    >
      <span className={cn('h-1.5 w-1.5 rounded-full', v.dot)} aria-hidden="true" />
      {children ?? v.defaultLabel}
      {variant === 'deploying' && (
        <Loader2
          className="size-3 text-gl-amber-text motion-safe:animate-spin"
          aria-hidden="true"
        />
      )}
    </span>
  );
}
